import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Auction } from '../types/auction.types';
import { format } from 'date-fns';
import BidFromTileModal from '../components/bidding/BidFromTileModal';
import SaleDisputeNotice from '../components/SaleDisputeNotice';
import { firstContentImage } from '../utils/lotImages';
import { computeLotDisplay, lotCategoryLabel, lotNumberLabel } from '../utils/lotDisplay';
import { useWatchlist } from '../hooks/useWatchlist';
import WatchButton from '../components/bidding/WatchButton';
import { useAuctionClock } from '../hooks/useAuctionClock';
import { MARKET_SITE_URL } from '../config/betweenSales';
import { serverNow } from '../utils/serverClock';
import { LotClock, LotStateBadge } from '../components/auction/LotClock';
import {
  SaleState,
  lotState,
  isLotBiddableState,
  soonestLotEndMs,
  deriveSaleState,
  isBiddable,
  isPubliclyVisible,
  isSaleOpen,
  isLotInSoftClose,
  isUnderReview,
  saleStateLabelFor,
  closedLotWord,
  DISPUTE_WORDS,
  closedLotStatus,
  isDisputeStatus,
  closedPriceSuffix,
  relistCount,
  DEFAULT_COOL_OFF_WINDOW,
} from '../utils/saleState';

/* Bids are stored in CENTS app-wide (300 = $3.00), matching the admin page. */
const formatPrice = (n: any): string => `$${((Number(n) || 0) / 100).toFixed(2)}`;

/* Primary product photo for a tile — shared helper skips the "{sku}_00" barcode frame.
   Returns '' when a lot has only internal frames, so the placeholder shows instead. */
const primaryImage = (lot: any): string => firstContentImage(lot?.images);

const toDate = (v: any): Date | null => {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (v instanceof Date) return v;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const durationLabel = (seconds: number): string => {
  if (seconds % 3600 === 0) { const h = seconds / 3600; return `${h} hour${h === 1 ? '' : 's'}`; }
  const m = Math.round(seconds / 60);
  return `${m} minute${m === 1 ? '' : 's'}`;
};

interface SaleWithLots extends Auction {
  _start: Date | null;
  _end: Date | null;
  _state: SaleState;
}

/**
 * The Schmidt-Market buy site -- where the open-slot tile below sends people.
 *
 * VERIFIED REACHABLE 2026-10-05: https://schmidt-market.web.app/market-board
 * serves the live board ("What Schmidt Auctions is buying", 96 Morgan positions).
 *
 * This is the FIREBASE DEFAULT domain, not the intended one. market.schmidtauctions.com
 * does not resolve -- the CNAME was scoped but never cut over (schmidtauctions.com is
 * a Wix site holding the apex). When that lands, change this one line; nothing else
 * in this file knows the URL.
 */
// MARKET_SITE_URL now lives in config/betweenSales.ts (one copy, shared with the home page).

const STATE_BADGE: Record<SaleState, string> = {
  draft: 'bg-gray-400 text-white',
  scheduled: 'bg-blue-500 text-white',
  live: 'bg-green-500 text-white animate-pulse',
  closing: 'bg-red-600 text-white animate-pulse',
  ended: 'bg-amber-500 text-white',
  confirmed: 'bg-gray-700 text-white',
};

/**
 * LOBBY ORDER: SOONEST CLOSE FIRST. A rule, not a fix.
 *
 * WHAT ORDERED THEM BEFORE: nothing. The snapshot is
 * `onSnapshot(collection(db, 'auctions'))` with no orderBy, so Firestore returns
 * documents by DOCUMENT ID ascending -- and these are random auto-ids. Ed's sale
 * led the lobby because 'H39uo0...' sorts before 'safONK...', which is a fact
 * about byte order and nothing about the sales. The third sale lands wherever
 * its random id happens to fall.
 *
 * WHY SOONEST FIRST: the sale that closes first is the one with urgency, so it
 * is the one a bidder needs to see first. `_end` is the auction's endDate -- the
 * FIRST lot's close -- so this orders sales by when each STARTS closing, which
 * is the signal a bidder acts on.
 *
 * TOTAL ORDER, DELIBERATELY. Equal endDates fall back to title and then id, so
 * the lobby cannot reshuffle between renders. This list re-derives on every
 * clock tick, so an unstable comparator would visibly flicker. A sale with NO
 * endDate sinks below every sale that has one: it makes no claim about closing,
 * so it cannot claim urgency.
 *
 * The open-slot tile is NOT in this sort -- it is rendered after the map, so it
 * is last by position. Don't give it a fake endDate to keep it there.
 */
const bySoonestClose = (a: SaleWithLots, b: SaleWithLots): number => {
  const am = a._end ? a._end.getTime() : null;
  const bm = b._end ? b._end.getTime() : null;
  if (am !== bm) {
    if (am === null) return 1;
    if (bm === null) return -1;
    return am - bm;
  }
  return (a.title || '').localeCompare(b.title || '') || a.id.localeCompare(b.id);
};

const PublicAuctionList: React.FC = () => {
  const [sales, setSales] = useState<SaleWithLots[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const preview = searchParams.get('preview') === 'true';
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Watchlist (shared with SalePage). One read; drives the ☆/★ on every tile.
  const { isWatched, toggle: toggleWatch } = useWatchlist();

  // Clock is installed after `selected` is derived (needs the lots to pick a cadence).

  // Soft-close bidding happens in the confirm modal, launched straight from the tile.
  const [bidTarget, setBidTarget] = useState<{ sale: SaleWithLots; lot: any } | null>(null);

  // Local-only lot-grid view controls (shared logic with SalePage via computeLotDisplay).
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [noBidsOnly, setNoBidsOnly] = useState(false);

  // 🔴 LIVE, not a one-shot read. This used to be getDocs() called once on mount,
  // with a 1s interval that only re-rendered STALE data — so a soft-close extension
  // (written server-side into auctions/{id}.lots[].actualEndTime inside the bid
  // transaction) never reached this page, and any countdown here would have run to
  // 0:00 and stuck while the lot was still taking bids. onSnapshot is what makes a
  // clock on these tiles honest. Read path only — no rules or bidding change.
  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      collection(db, 'auctions'),
      (snap) => {
        const all: SaleWithLots[] = snap.docs.map((d) => {
          const data = d.data() as any;
          const a = { id: d.id, ...data, lots: data.lots || [] } as Auction;
          return {
            ...(a as any),
            _start: toDate(data.startDate),
            // endDate is the FIRST lot's close, not the sale's end -- the convention
            // create-ed-sale.mjs asserts against the September sale. Labelled
            // "first lot closes" everywhere this is rendered: for Ed's sale the
            // first lot closes 8:00:00 PM, the last 8:22:32, and a soft-close
            // extension can carry a lot to 8:52:32. "closes 8:00 PM" told a bidder
            // the sale ends at the moment it starts ending.
            _end: toDate(data.endDate),
            _state: deriveSaleState(a),
          } as SaleWithLots;
        });

        // 🔴 STATE-DRIVEN visibility. The scheduled endDate NEVER hides a sale — that check is
        // what blanked the homepage at 8 PM while lots were still open in soft-close.
        const visible = all.filter((s) => {
          if (!isPubliclyVisible(s)) return false;             // drafts stay private
          if (s._state === 'scheduled') return preview;         // upcoming only in preview
          return true;                                          // live / closing / ended / confirmed
        });

        // Most recent first — drives "persist the last sale until a new catalog is imported".
        visible.sort((a, b) => (b._start?.getTime() || 0) - (a._start?.getTime() || 0));
        setSales(visible);
        setLoading(false);
      },
      (err) => {
        console.error('Sales snapshot error:', err);
        setLoading(false);
      },
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  // ONE clock for the page (adaptive: 1s when a lot is near, 15s otherwise). Replaces
  // the old unconditional 1s interval, which re-rendered stale data once per second.
  const soonestEnd = useMemo(() => {
    const nowRef = serverNow();
    let soonest: number | null = null;
    for (const s of sales) {
      const t = soonestLotEndMs(s.lots || [], nowRef);
      if (t !== null && (soonest === null || t < soonest)) soonest = t;
    }
    return soonest;
  }, [sales]);
  const nowMs = useAuctionClock(soonestEnd);

  // Re-derive state on every tick so a lot entering soft-close flips live -> closing live.
  const now = new Date(nowMs);
  const withState = sales.map((s) => ({ ...s, _state: deriveSaleState(s, now) }));
  const active = withState.filter((s) => isSaleOpen(s, now)).sort(bySoonestClose);
  // Selection: any active sale wins; otherwise fall back to the most recent sale so the
  // homepage is NEVER blank — it shows that sale's current state (ended / confirmed).
  const selected: SaleWithLots | null = active.length === 1 ? active[0] : active.length === 0 ? (withState[0] ?? null) : null;
  const showLobby = active.length > 1;
  // Shared lot ordering/filtering (same module SalePage uses). Pure — safe when no selection.
  const selectedDisplay = computeLotDisplay(selected?.lots || [], { groupByCategory, noBidsOnly });

  const goLot = (auctionId: string, lotId?: string) =>
    navigate(lotId ? `/auctions/${auctionId}/lots/${lotId}` : `/sale/${auctionId}`);

  // B — in soft-close, the tile Bid launches the confirm modal (fewest taps during the race).
  const handleTileClick = (sale: SaleWithLots, lot: any) => {
    if (sale._state === 'closing' && isLotInSoftClose(lot, sale, new Date(nowMs))) {
      setBidTarget({ sale, lot });
      return;
    }
    goLot(sale.id, lot.id);
  };

  if (loading) {
    return <div className="text-center py-12"><p className="text-gray-600">Loading…</p></div>;
  }

  const coolOffSecs = selected?.coolOffWindow ?? DEFAULT_COOL_OFF_WINDOW;

  return (
    <div className="space-y-8">
      {preview && (
        <div className="bg-amber-500 text-white text-center py-2 px-4 rounded-lg font-semibold text-sm shadow">
          👁 PREVIEW MODE — you're seeing scheduled sales that aren't public yet.
        </div>
      )}

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div className="text-center py-6">
        {showLobby ? (
          <>
            <h1 className="text-4xl font-bold mb-2">Live Auctions</h1>
            <p className="text-gray-600">{active.length} sales are open — choose one to bid</p>
          </>
        ) : selected ? (
          <>
            <div className="flex items-center justify-center gap-3 mb-2">
              <h1 className="text-4xl font-bold">{selected.title}</h1>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATE_BADGE[selected._state]}`}>
                {saleStateLabelFor(selected._state, selected).toUpperCase()}
              </span>
            </div>
            <p className="text-gray-600">
              {isBiddable(selected._state)
                ? <>{selected._end ? `First lot closes ${format(selected._end, 'MMM d, h:mm a')} · ` : ''}{selected.lots.length} lots · 3-minute soft close</>
                : <>{selected.lots.length} lots</>}
            </p>
          </>
        ) : (
          <>
            <h1 className="text-4xl font-bold mb-2">Auctions</h1>
            <p className="text-gray-600">No sales to show yet — check back soon.</p>
          </>
        )}
      </div>

      {/* ── Lobby: multiple sales open simultaneously (Whatnot-style) ─────── */}
      {showLobby && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {active.map((sale) => (
            <Link
              key={sale.id}
              to={`/sale/${sale.id}`}
              className="card hover:shadow-xl transition-all flex flex-col gap-2"
            >
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-lg font-bold">{sale.title}</h2>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap ${STATE_BADGE[sale._state]}`}>
                  {saleStateLabelFor(sale._state, sale).toUpperCase()}
                </span>
              </div>
              {sale.auctionHouseName && <p className="text-sm text-gray-600">{sale.auctionHouseName}</p>}
              <p className="text-sm text-gray-500">
                {sale.lots.length} lots
                {sale._end ? ` · first lot closes ${format(sale._end, 'MMM d, h:mm a')}` : ''}
              </p>
              <span className="mt-2 text-sm font-semibold text-primary-600">Enter sale →</span>
            </Link>
          ))}

          {/* ── OPEN SLOT ─────────────────────────────────────────────────
              Always present, and deliberately NOT a sale: dashed rather than
              solid, no state badge, no lot count, muted until hovered. It sits
              in the same grid so it reads as the next position in the row --
              which is the point -- but nothing about it should scan as
              something you can bid on.

              External link, so <a> and not <Link>. Opens in a new tab: a
              bidder who clicks it mid-sale should not lose the lots.         */}
          <a
            href={MARKET_SITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-xl border-2 border-dashed border-gray-300 bg-transparent p-6
                       flex flex-col gap-2 transition-all
                       hover:border-primary-400 hover:bg-white hover:shadow-lg"
          >
            <p className="text-xs font-bold tracking-wide text-gray-400">OPEN SLOT</p>
            <h2 className="text-lg font-bold text-gray-800">Your collection could be here</h2>
            <p className="text-sm text-gray-600">
              We run one collection at a time so it gets the whole room.
            </p>
            <p className="text-sm text-gray-600">
              Cash advances on collections over $10,000.
            </p>
            <span className="mt-2 text-sm font-semibold text-primary-600">
              Tell us what you have →
            </span>
          </a>
        </div>
      )}

      {/* ── Selected sale ─────────────────────────────────────────────────── */}
      {selected && !showLobby && (
        <section className="space-y-4">
          {/* DISPUTED: the sale's own notice replaces the ended / final banners below. */}
          {isUnderReview(selected) && selected.disputeNotice && (
            <SaleDisputeNotice text={selected.disputeNotice} relistCount={relistCount(selected)} relistUpdatedAt={selected.disputedLotsUpdatedAt} relistUpdateNote={selected.disputedLotsUpdateNote} />
          )}
          {/* ENDED (cool-off): explain the tally window, show NO prices. */}
          {selected._state === 'ended' && !isUnderReview(selected) && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
              <p className="font-bold text-amber-900">Bidding is closed — bids are being tallied &amp; confirmed.</p>
              <p className="text-sm text-amber-800 mt-1">
                Final prices are not official until the auctioneer confirms them. Per the sale terms, there is a
                cool-off / tally window of about {durationLabel(coolOffSecs)} after the last lot closes.
                {' '}<Link to={`/sale/${selected.id}/info`} className="underline">See sale terms</Link>.
              </p>
            </div>
          )}
          {selected._state === 'confirmed' && !isUnderReview(selected) && (
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
              <p className="font-bold text-gray-900">Results are final.</p>
              <p className="text-sm text-gray-700 mt-1">
                The auctioneer has confirmed these results. Prices shown are official.
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 mb-2">
            <button
              type="button"
              onClick={() => setGroupByCategory((v) => !v)}
              className={`px-3 py-1.5 text-sm rounded-full border ${
                groupByCategory
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Sort by Category
            </button>
            <button
              type="button"
              onClick={() => setNoBidsOnly((v) => !v)}
              className={`px-3 py-1.5 text-sm rounded-full border ${
                noBidsOnly
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Show Lots w No Bids
            </button>
            <span className="text-sm text-gray-500 ml-auto">
              Showing {selectedDisplay.shownCount} of {selectedDisplay.totalCount} lots
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {selectedDisplay.lots.length === 0 ? (
              <p className="col-span-full text-center text-gray-500 py-10">
                No lots match the current filter.
              </p>
            ) : (
            selectedDisplay.lots.map((lot: any, i: number) => {
              const img = primaryImage(lot);
              const startingBid = lot.startingBid ?? 0;
              const hasRealBid = lot.currentBid != null && lot.currentBid > startingBid;
              // Shared vocabulary — the SAME lotState() the sale page's LotCard uses.
              const lState = lotState(lot, selected, nowMs);
              // Dispute display (saleState.ts): 'closed' | 'under-review' | 'to-be-relisted'.
              const cStatus = closedLotStatus(selected, lot);
              const biddable = isBiddable(selected._state) && isLotBiddableState(lState);
              const inSoftClose = biddable && (lState.kind === 'closing_now' || lState.kind === 'settling');
              const showCatHeader =
                groupByCategory && (i === 0 || lotCategoryLabel(selectedDisplay.lots[i - 1]) !== lotCategoryLabel(lot));

              return (
                <React.Fragment key={lot.id || lot.sku || i}>
                  {showCatHeader && (
                    <h2 className="col-span-full text-lg font-bold text-gray-700 border-b border-gray-200 pb-1 mt-2">
                      {lotCategoryLabel(lot)}
                    </h2>
                  )}
                <div
                  onClick={() => biddable && handleTileClick(selected, lot)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && biddable && handleTileClick(selected, lot)}
                  className={`card flex flex-col p-0 overflow-hidden transition-all ${
                    biddable ? 'cursor-pointer hover:shadow-xl group' : ''
                  } ${inSoftClose ? 'ring-2 ring-red-500' : ''} ${
                    lState.kind === 'closed_sold' || lState.kind === 'closed_unsold' ? 'opacity-90' : ''
                  }`}
                  role={biddable ? 'button' : undefined}
                  tabIndex={biddable ? 0 : undefined}
                >
                  {/* Single relative image wrapper (matches SalePage tiles) so the Watch
                      star sits over BOTH real images and the placeholder. */}
                  <div className="aspect-square overflow-hidden bg-gray-100 relative">
                    {img ? (
                      <img src={img} alt={lot.title} loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center">
                        <span className="text-4xl">🔨</span>
                      </div>
                    )}
                    <LotStateBadge state={lState} closedStatus={cStatus} className="absolute top-2 right-2" />
                    <WatchButton
                      variant="icon"
                      watched={isWatched(selected.id, lot.id)}
                      onToggle={() => toggleWatch({ auctionId: selected.id, lotId: lot.id, lotNumber: lot.lotNumber, lotTitle: lot.title, addedAt: Date.now() })}
                      className="absolute bottom-2 left-2 z-10"
                    />
                  </div>

                  <div className="p-3 flex flex-col flex-1">
                    {/* Lot number leads the card — a 138-lot catalogue is unnavigable
                        without it. Same lotNumberLabel() the lot page and SalePage
                        tiles use, so the three can't drift. SKU stays, demoted. */}
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <span className="text-sm font-bold text-gray-900 font-mono">
                        {lotNumberLabel(lot)}
                      </span>
                      <LotClock state={lState} closedStatus={cStatus} />
                    </div>
                    <p className="text-[11px] text-gray-400 font-mono truncate mb-1">SKU {lot.sku}</p>
                    <h3 className="text-sm font-semibold leading-snug mb-2 line-clamp-2 group-hover:text-primary-600 transition">
                      {lot.title}
                    </h3>

                    <div className="mt-auto">
                      {/* Results are now PER-LOT and stay visible through the cool-off,
                          marked provisional until the auctioneer confirms. The sale-level
                          'ended' branch used to blank every price the moment the last lot
                          closed, which hid results the tiles had shown all evening. */}
                      {lState.kind === 'closed_sold' ? (
                        <>
                          <p className="text-xs text-gray-500">
                            {isDisputeStatus(cStatus) ? DISPUTE_WORDS.highBid : selected._state === 'confirmed' ? 'Final price' : 'Sold (provisional)'}
                          </p>
                          <p className="text-lg font-bold text-gray-900">
                            {formatPrice(lState.hammerCents ?? 0)}
                            {isDisputeStatus(cStatus) && (
                              <span className="ml-1 text-xs font-semibold text-amber-800">{closedPriceSuffix(cStatus)}</span>
                            )}
                          </p>
                          {/* PUBLIC bidder number only — never a name. */}
                          <p className="text-[11px] text-gray-500 mb-2">
                            {currentUser && lState.winnerUid === currentUser.uid
                              ? (isDisputeStatus(cStatus) ? DISPUTE_WORDS.youAreHighBidder : 'You won')
                              : lState.winnerBidderNumber
                                ? `Bidder #${lState.winnerBidderNumber}`
                                : '\u00A0'}
                          </p>
                        </>
                      ) : lState.kind === 'closed_unsold' ? (
                        <>
                          <p className="text-xs text-gray-500">{closedLotWord(cStatus, 'sentence')}</p>
                          <p className="text-lg font-bold text-gray-400 mb-2">Unsold</p>
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-gray-500">{hasRealBid ? 'Current bid' : 'Starting bid'}</p>
                          <p className="text-lg font-bold text-green-600 mb-2">
                            {formatPrice(hasRealBid ? lot.currentBid : startingBid)}
                          </p>
                        </>
                      )}

                      {biddable && (
                        <div className={`w-full text-center text-sm font-semibold py-2 rounded-lg ${
                          inSoftClose
                            ? 'bg-red-600 text-white group-hover:bg-red-700'
                            : currentUser
                              ? 'bg-primary-600 text-white group-hover:bg-primary-700'
                              : 'bg-gray-100 text-gray-700 group-hover:bg-gray-200'
                        }`}>
                          {inSoftClose ? 'Bid Now — Closing!' : currentUser ? 'Bid Now' : 'Sign In to Bid'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                </React.Fragment>
              );
            })
          )}
          </div>
        </section>
      )}

      {/* B — soft-close confirm modal, reusing the SalePage component. */}
      {bidTarget && (
        <BidFromTileModal
          /* LIVE lookup, every render: the onSnapshot listener refreshes `sales`, and the modal
             must see extensions and new high bids. bidTarget only remembers WHICH lot was
             clicked; the captured copy is a fallback if the lot vanishes from the snapshot. */
          auction={sales.find((s) => s.id === bidTarget.sale.id) ?? bidTarget.sale}
          lot={
            (sales.find((s) => s.id === bidTarget.sale.id)?.lots || []).find((l: any) => l.id === bidTarget.lot.id)
            ?? bidTarget.lot
          }
          onClose={() => setBidTarget(null)}
          /* The onSnapshot subscription already refreshes the grid live. */
          onBidPlaced={() => {}}
        />
      )}
    </div>
  );
};

export default PublicAuctionList;
